import RouteTemplate from "ember-route-template";
import { on } from "@ember/modifier";
import { fn, array } from "@ember/helper";
import eq from "truth-helpers/helpers/eq";
import not from "truth-helpers/helpers/not";

export default RouteTemplate(
  <template>
    <section class="proxyviews-directory-page">
      {{#if @controller.loadError}}
        <div class="alert alert-error">
          Couldn't load benchmark data right now. Please try refreshing the page.
        </div>
      {{/if}}

      <div class="pd-hero">
        <span class="pd-hero__eyebrow">Performance Lab</span>
        <h1 class="pd-hero__title">Proxy <span>benchmarks</span></h1>
        <p class="pd-hero__subtitle">
          Real performance data — response times, success rates, and block rates —
          measured across providers, regions, and protocols.
        </p>
        <div class="pd-hero__stats">
          <div class="pd-hero__stat">
            <span class="pd-hero__stat-value">{{@controller.meta.total_count}}</span>
            <span class="pd-hero__stat-label">Test Runs</span>
          </div>
          <div class="pd-hero__stat">
            <span class="pd-hero__stat-value">{{@controller.meta.providers_tested}}</span>
            <span class="pd-hero__stat-label">Providers</span>
          </div>
          {{#if @controller.avgResponseTimeDisplay}}
            <div class="pd-hero__stat">
              <span class="pd-hero__stat-value">{{@controller.avgResponseTimeDisplay}}</span>
              <span class="pd-hero__stat-label">Avg Latency</span>
            </div>
          {{/if}}
          {{#if @controller.formattedLatestRunDate}}
            <div class="pd-hero__stat">
              <span class="pd-hero__stat-value pd-hero__stat-value--date">{{@controller.formattedLatestRunDate}}</span>
              <span class="pd-hero__stat-label">Latest Test</span>
            </div>
          {{/if}}
        </div>
      </div>

      <div class="pd-list-controls pd-benchmarks-controls">
        <div class="pd-benchmarks-controls__filters">
          <select class="pd-benchmarks-filter" aria-label="Filter by test type" {{on "change" @controller.filterByTestType}}>
            <option value="">All test types</option>
            {{#each @controller.testTypes as |t|}}
              <option value={{t}} selected={{eq @controller.testType t}}>{{t}}</option>
            {{/each}}
          </select>
          <select class="pd-benchmarks-filter" aria-label="Filter by region" {{on "change" @controller.filterByRegion}}>
            <option value="">All regions</option>
            {{#each @controller.regions as |rg|}}
              <option value={{rg}} selected={{eq @controller.region rg}}>{{rg}}</option>
            {{/each}}
          </select>
          <select class="pd-benchmarks-filter" aria-label="Filter by provider" {{on "change" @controller.filterByProvider}}>
            <option value="">All providers</option>
            {{#each @controller.providerList as |p|}}
              <option value={{p}} selected={{eq @controller.providerFilter p}}>{{p}}</option>
            {{/each}}
          </select>
          <select class="pd-benchmarks-filter" aria-label="Sort by" {{on "change" @controller.changeSortBy}}>
            <option value="response" selected={{eq @controller.sortBy "response"}}>Fastest Response</option>
            <option value="success" selected={{eq @controller.sortBy "success"}}>Highest Success</option>
            <option value="grade" selected={{eq @controller.sortBy "grade"}}>Best Grade</option>
            <option value="provider" selected={{eq @controller.sortBy "provider"}}>Provider A–Z</option>
            <option value="date" selected={{eq @controller.sortBy "date"}}>Most Recent</option>
          </select>
        </div>

        {{#if @controller.selectedRuns.length}}
          <button type="button" class="btn btn-primary btn-small pd-benchmarks-compare-btn" {{on "click" @controller.openCompare}}>
            Compare Selected ({{@controller.selectedRuns.length}})
          </button>
        {{/if}}
      </div>

      {{#if @controller.meta.total_count}}
        <p class="pd-result-count">
          {{@controller.meta.total_count}} benchmark runs
          {{#if @controller.testType}} · {{@controller.testType}}{{/if}}
          {{#if @controller.region}} · {{@controller.region}}{{/if}}
          {{#if @controller.providerFilter}} · {{@controller.providerFilter}}{{/if}}
        </p>
      {{/if}}

      <div class="pd-grid">
        {{#if @controller.loading}}
          {{#each (array 1 2 3 4 5 6) as |skeleton|}}
            <article class="pd-benchmark__card pd-benchmark__card--skeleton">
              <div class="pd-benchmark__provider">
                <div class="benchmark-logo-container pd-skeleton-circle"></div>
                <div class="pd-benchmark__provider-info">
                  <div class="pd-skeleton-line pd-skeleton-line--title"></div>
                  <div class="pd-skeleton-line pd-skeleton-line--small"></div>
                </div>
                <div class="pd-skeleton-badge"></div>
              </div>
              <div class="pd-benchmark__metrics-visual">
                <div class="pd-skeleton-line pd-skeleton-line--bar"></div>
                <div class="pd-skeleton-line pd-skeleton-line--bar"></div>
                <div class="pd-skeleton-line pd-skeleton-line--bar"></div>
              </div>
              <div class="pd-benchmark__meta">
                <div class="pd-skeleton-tag"></div>
                <div class="pd-skeleton-tag"></div>
                <div class="pd-skeleton-tag"></div>
              </div>
            </article>
          {{/each}}
        {{else}}
          {{#each @controller.sortedRuns as |r|}}
            <article class="pd-benchmark__card">
              <div class="pd-benchmark__select">
                <input
                  type="checkbox"
                  checked={{r.selected}}
                  {{on "change" (fn @controller.toggleRunSelection r)}}
                  aria-label="Select {{r.provider_name}} for comparison"
                />
              </div>

              <div class="pd-benchmark__provider">
                <div class="benchmark-logo-container">
                  {{#if r.provider_logo}}
                    <img src={{r.provider_logo}} alt={{r.provider_name}} class="benchmark-provider-logo" loading="lazy" {{on "error" @controller.handleLogoError}} />
                    <span class="benchmark-provider-logo-fallback" style="display:none">{{r.provider_initial}}</span>
                  {{else}}
                    <span class="benchmark-provider-logo-fallback">{{r.provider_initial}}</span>
                  {{/if}}
                </div>
                <div class="pd-benchmark__provider-info">
                  <h3 class="pd-benchmark__provider-name">{{r.provider_name}}</h3>
                  <a href="/providers/{{r.provider_slug}}" class="pd-benchmark__provider-link">View Profile &rarr;</a>
                </div>
                <span class="pd-benchmark__grade {{r.gradeClass}}">{{r.gradeLetter}}</span>
              </div>

              <div class="pd-benchmark__metrics-visual">
                <div class="pd-progress-item">
                  <div class="pd-progress-header">
                    <span class="label">Avg Response</span>
                    <span class="value">{{r.responseDisplay}}</span>
                  </div>
                  <div class="pd-progress-track">
                    <div class="pd-progress-fill {{r.responseGradeClass}}" style={{r.responseStyle}}></div>
                  </div>
                </div>
                <div class="pd-progress-item">
                  <div class="pd-progress-header">
                    <span class="label">Success Rate</span>
                    <span class="value">{{r.successDisplay}}</span>
                  </div>
                  <div class="pd-progress-track">
                    <div class="pd-progress-fill pd-progress-fill--green" style={{r.successStyle}}></div>
                  </div>
                </div>
                <div class="pd-progress-item">
                  <div class="pd-progress-header">
                    <span class="label">Block Rate</span>
                    <span class="value">{{r.blockDisplay}}</span>
                  </div>
                  <div class="pd-progress-track">
                    <div class="pd-progress-fill pd-progress-fill--red" style={{r.blockStyle}}></div>
                  </div>
                </div>
                <div class="pd-progress-item pd-progress-item--meta">
                  <div class="pd-progress-header">
                    <span class="label">Region · Protocol</span>
                    <span class="value">{{r.region}} · {{r.protocol}}</span>
                  </div>
                </div>
              </div>

              <div class="pd-benchmark__footer">
                <div class="pd-benchmark__meta">
                  <span class="pd-tag">{{r.test_type}}</span>
                  <span class="pd-tag">{{r.concurrent_connections}} conn</span>
                  <span class="pd-tag">{{r.total_requests}} req</span>
                  <span class="pd-benchmark__source-badge {{r.sourceClass}}">{{r.sourceLabel}}</span>
                </div>
                {{#if r.tested_at}}
                  <span class="pd-benchmark__date">{{r.tested_at_display}}</span>
                {{/if}}
              </div>
            </article>
          {{else}}
            <div class="pd-empty">
              <div class="pd-empty__icon">&#128202;</div>
              <div class="pd-empty__title">No benchmark runs found</div>
              <p class="pd-empty__body">No benchmarks match your current filters, or none have been published yet.</p>
              <a href="/providers" class="btn btn-primary pd-empty__cta">Browse Providers</a>
            </div>
          {{/each}}
        {{/if}}
      </div>

      {{#unless (eq @controller.totalPages 1)}}
        <nav class="pd-admin-pagination" aria-label="Benchmark pages">
          <button type="button" class="btn btn-default btn-small" disabled={{not @controller.hasPrevPage}} aria-label="Previous page" {{on "click" @controller.prevPage}}>&larr; Previous</button>
          <span class="pd-admin-pagination__status">Page {{@controller.page}} of {{@controller.totalPages}}</span>
          <button type="button" class="btn btn-default btn-small" disabled={{not @controller.hasNextPage}} aria-label="Next page" {{on "click" @controller.nextPage}}>Next &rarr;</button>
        </nav>
      {{/unless}}
    </section>

  </template>
);
